import React from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import IndicatorContentIcon from './IndicatorContentIcon';
import { IndicatorSectionIcon } from '../ImpactSummary/SummaryIcon';
import { IndicatorSectionKey } from '../ImpactIndicator/utils';
import { IndicatorDetails } from './utils';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';

interface IndicatorSectionContentProps {
  sectionKey: IndicatorSectionKey;
  sectionName: string;
  indicator: IndicatorDetails;
  totalIndicators: number;
  index: number;
  className?: string;
}

const StyledDiv = styled.div`
  padding-left: 58px;
  padding-top: ${SPACERS.medium};
  .section-header {
    display: flex;
    align-items: center;
    color: ${COLORS.blackBase};
    .left-header {
      display: flex;
      max-width: 159px;
      align-items: center;
      padding-right: ${SPACERS.medium};
      border-right: 1px solid ${COLORS.grayDark};
      svg {
        width: 40px;
        height: 40px;
        margin-right: ${SPACERS.tiny};
        path {
          stroke: ${COLORS.ctaBrandOrange};
        }
      }
      .header-label {
        color: inherit;
        margin-bottom: 0;
      }
    }
    .right-header {
      padding-left: ${SPACERS.medium};
      .capsule-container {
        display: flex;
        align-items: center;
        margin-top: ${SPACERS.tiny};
        .capsule {
          width: 16px;
          height: 8px;
          border-radius: 16px;
          margin-right: ${SPACERS.extraTiny};
          border: 1px solid ${COLORS.grayDarkest};
        }
        .capsule.selected {
          background-color: ${COLORS.grayDarkest};
        }
      }
    }
  }
  .section-content {
    display: flex;
    padding-right: 52px;
    margin-top: 22px;
    .section-content-heading {
      margin-bottom: ${SPACERS.tiny};
    }
    .left-section-content {
      flex: 1;
      margin-right: 12px;
    }
    .right-section-content {
      flex: 1;
      margin-left: 12px;
      .margin-bottom {
        margin-bottom: 12px;
      }
    }
  }
`;

const Capsules: React.FC<{
  totalIndicators: IndicatorSectionContentProps['totalIndicators'];
  index: IndicatorSectionContentProps['index'];
  className?: string;
}> = ({ totalIndicators, index, className }) => {
  const result: JSX.Element[] = [];
  for (let i = 0; i < totalIndicators; i++) {
    result.push(
      <div className={i === index ? `${className} selected` : className} />,
    );
  }
  return <>{result}</>;
};

const IndicatorSectionContent: React.FC<IndicatorSectionContentProps> = ({
  sectionKey,
  sectionName,
  totalIndicators,
  index,
  indicator,
  className,
}) => {
  return (
    <StyledDiv className={className}>
      <div className="section-header">
        <div className="left-header">
          <IndicatorSectionIcon sectionKey={sectionKey} />
          <TrueImpactText variant="heading-label" className="header-label">
            {sectionName}
          </TrueImpactText>
        </div>
        <div className="right-header">
          <TrueImpactText variant="body-small-bold">
            {totalIndicators === 1 ? '1 impact' : `${totalIndicators} impacts`}
          </TrueImpactText>
          <div className="capsule-container">
            <Capsules
              totalIndicators={totalIndicators}
              index={index}
              className="capsule"
            />
          </div>
        </div>
      </div>
      <div className="section-content">
        <div className="left-section-content">
          <TrueImpactText
            variant="body-bold"
            className="section-content-heading"
          >
            Forecasted Indicator:
          </TrueImpactText>
          <IndicatorContentIcon
            state={indicator.forecast ? 'checkmark' : 'question'}
            text={indicator.forecast?.toString()}
          />
          <IndicatorContentIcon
            state={undefined}
            text={indicator.indicatorName}
          />
        </div>
        <div className="right-section-content">
          <TrueImpactText
            variant="body-bold"
            className="section-content-heading"
          >
            Time period:
          </TrueImpactText>
          <IndicatorContentIcon
            state={indicator.timePeriod ? 'checkmark' : 'question'}
            text={indicator.timePeriod}
            className="margin-bottom"
          />
          <TrueImpactText
            variant="body-bold"
            className="section-content-heading"
          >
            How will you know this?
          </TrueImpactText>
          <IndicatorContentIcon
            state={indicator.measurement ? 'checkmark' : 'question'}
            text={indicator.measurement}
          />
        </div>
      </div>
    </StyledDiv>
  );
};

export default IndicatorSectionContent;
